import { FC, useMemo, useState } from 'react';

import type {
  ActivityEntryKind,
  MemberActivityContact,
  MemberActivityDeal,
  MemberActivityResponse,
} from '@/api/gamma-team/types';
import { Alert, Button, EmptyState, Link, TextButton, theme, Typography } from '@/design-system';
import { buildNoteObjectPath } from '@/utils/build-note-object-path';

import { COMMUNICATION_KIND_LABELS } from '../../user-profile/modules/history-tab/constants';
import { formatEntryTimestamp } from '../../user-profile/modules/history-tab/format-entry-time';
import { ContactActivity } from './contact-activity';
import {
  ContactCard,
  ContactList,
  QuietLine,
  Section,
  SectionActions,
  SectionHeader,
  SkeletonBar,
} from './styled';
import { SummaryCards } from './summary-cards';
import { memberName } from './team-sorting';

export const TRUNCATED_NOTICE =
  'This period has more entries than the report can show. Only the most recent 2000 are listed — narrow the period to see everything.';

export const emptyMemberActivityMessage = (name: string, kind: ActivityEntryKind | null): string =>
  kind
    ? `No ${COMMUNICATION_KIND_LABELS[kind]} entries for ${name} in this period.`
    : `No Contact History entries for ${name} in this period.`;

export const filterContactsByKind = (
  contacts: MemberActivityContact[],
  kind: ActivityEntryKind | null,
): MemberActivityContact[] => {
  if (!kind) return contacts;

  return contacts
    .filter((contact) => contact.kinds.includes(kind))
    .map((contact) => {
      const items = contact.items.filter((entry) => entry.kind === kind);
      return { ...contact, items, entries: items.length, kinds: [kind] };
    });
};

const ContactsSkeleton: FC = () => (
  <ContactList aria-hidden>
    {[0, 1, 2].map((index) => (
      <ContactCard key={index}>
        <SkeletonBar w="180px" />
        <SkeletonBar w="60%" />
      </ContactCard>
    ))}
  </ContactList>
);

const DEAL_STAGE_LABELS: Record<MemberActivityDeal['stage'], string> = {
  Interested: 'Interested',
  NdaSigned: 'NDA signed',
  BuyerSellerConnected: 'Buyer / seller connected',
  DueDiligence: 'Due diligence',
  KycKyb: 'KYC / KYB',
  ContractSigned: 'Contract signed',
  MoneySent: 'Money sent',
  InvoiceSent: 'Invoice sent',
  FeesCollected: 'Fees collected',
  DealCanceled: 'Deal canceled',
  OfferDeleted: 'Offer deleted',
};

export const DealRow: FC<{ deal: MemberActivityDeal }> = ({ deal }) => {
  const href = deal.deleted ? null : buildNoteObjectPath('Deal', deal.entryId);
  const company = deal.companyName?.trim() || 'Unknown company';
  const buyer = deal.buyer.name?.trim() || 'Unknown buyer';
  return (
    <ContactCard>
      {href ? (
        <Link to={href} size="regular">
          {company}
        </Link>
      ) : (
        <Typography variant="body2">{company} · Deleted — unavailable on Pipeline</Typography>
      )}
      <Typography variant="body3" color={theme.colors.text.secondary}>
        {buyer} · {DEAL_STAGE_LABELS[deal.stage]}
      </Typography>
      <Typography variant="body3" color={theme.colors.text.secondary}>
        {[
          deal.createdAt && `Created ${formatEntryTimestamp(deal.createdAt)}`,
          deal.advancedAt && `Advanced ${formatEntryTimestamp(deal.advancedAt)}`,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Typography>
    </ContactCard>
  );
};

interface MemberActivityProps {
  data: MemberActivityResponse | undefined;
  isPending: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
}

export const MemberActivity: FC<MemberActivityProps> = ({
  data,
  isPending,
  isError,
  isFetching,
  onRetry,
}) => {
  const [kind, setKind] = useState<ActivityEntryKind | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const contacts = useMemo(
    () => filterContactsByKind(data?.contacts ?? [], kind),
    [data?.contacts, kind],
  );

  const toggleKind = (next: ActivityEntryKind) =>
    setKind((current) => (current === next ? null : next));

  const toggleContact = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = () => setExpanded(new Set(contacts.map((item) => item.contact.id)));
  const collapseAll = () => setExpanded(new Set());

  if (isError && !data) {
    return (
      <QuietLine role="alert">
        <Typography variant="body2" color={theme.colors.text.secondary}>
          Team activity couldn’t be loaded.
        </Typography>
        <Button
          variant="outline"
          color="tertiary"
          size="md"
          disabled={isFetching}
          onClick={onRetry}
        >
          {isFetching ? 'Loading…' : 'Try again'}
        </Button>
      </QuietLine>
    );
  }

  const name = data ? memberName(data.member) : 'this member';
  const allExpanded =
    contacts.length > 0 && contacts.every((item) => expanded.has(item.contact.id));

  return (
    <>
      <SummaryCards counts={data?.summary ?? null} selectedKind={kind} onToggleKind={toggleKind} />

      {data?.truncated && <Alert variant="warning" description={TRUNCATED_NOTICE} />}

      <Section>
        <SectionHeader>
          <Typography variant="body1" fontWeight={600}>
            Contacts
          </Typography>
          {contacts.length > 0 && (
            <SectionActions>
              <TextButton color="neutral" disabled={allExpanded} onClick={expandAll}>
                Expand all
              </TextButton>
              <TextButton color="neutral" disabled={expanded.size === 0} onClick={collapseAll}>
                Collapse all
              </TextButton>
            </SectionActions>
          )}
        </SectionHeader>

        {isPending || !data ? (
          <ContactsSkeleton />
        ) : contacts.length === 0 ? (
          <EmptyState
            variant="tertiary"
            role="status"
            title={emptyMemberActivityMessage(name, kind)}
          />
        ) : (
          <ContactList>
            {contacts.map((item) => (
              <ContactActivity
                key={item.contact.id}
                item={item}
                expanded={expanded.has(item.contact.id)}
                onToggle={() => toggleContact(item.contact.id)}
              />
            ))}
          </ContactList>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <Typography variant="body1" fontWeight={600}>
            Deals
          </Typography>
        </SectionHeader>
        {isPending || !data ? (
          <ContactsSkeleton />
        ) : data.deals.length === 0 ? (
          <EmptyState
            variant="tertiary"
            role="status"
            title={`No deals for ${name} in this period.`}
          />
        ) : (
          <ContactList>
            {data.deals.map((deal) => (
              <DealRow key={deal.entryId} deal={deal} />
            ))}
          </ContactList>
        )}
      </Section>
    </>
  );
};
